import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("odyssey_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.includes("/login")) {
      localStorage.removeItem("odyssey_token");
      localStorage.removeItem("odyssey_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// Auth
export const apiLogin = (username, password) => api.post("/auth/login", { username, password });
export const apiMe = () => api.get("/auth/me");

// Cases
export const getCases = (params) => api.get("/cases", { params });
export const getCase = (id) => api.get(`/cases/${id}`);
export const createCase = (data) => api.post("/cases", data);
export const addDocket = (id, entry) => api.post(`/cases/${id}/docket`, { entry });
export const updateCase = (id, data) => api.patch(`/cases/${id}`, data);
export const addEvent = (id, data) => api.post(`/cases/${id}/events`, data);
export const completeHearing = (id, index, data) => api.post(`/cases/${id}/hearings/${index}/complete`, data);
export const addCharge = (id, data) => api.post(`/cases/${id}/charges`, data);
export const createOrder = (id, data) => api.post(`/cases/${id}/orders`, data);
export const aiSummary = (id) => api.post(`/cases/${id}/ai-summary`);
export const predictOutcome = (id, data) => api.post(`/cases/${id}/predict`, data);
export const processExpungement = (id, data) => api.post(`/cases/${id}/expungement`, data);

// Public (no auth)
export const publicSearch = (q) => api.get(`/public/cases`, { params: { q } });
export const getPublicDisplay = (params) => api.get(`/public/display`, { params });
export const publicPay = (data) => api.post(`/public/pay`, data);
export const publicReceiptUrl = (receipt) => `${API}/public/payments/${receipt}/receipt`;

// Clerk financial functions
export const voidPayment = (id) => api.post(`/payments/${id}/void`);
export const waiveFees = (id, data) => api.post(`/cases/${id}/waive`, data);

// Intake, pretrial, warrants, bookings, reports, plans, worklist
export const getIntake = () => api.get(`/intake`);
export const advanceIntake = (id) => api.post(`/cases/${id}/intake/advance`);
export const setIntakeStage = (id, stage) => api.post(`/cases/${id}/intake/stage`, { stage });
export const reduceCharge = (id, data) => api.post(`/cases/${id}/reduce-charge`, data);
export const getPretrial = (id) => api.get(`/cases/${id}/pretrial`);
export const recordAssessment = (id, data) => api.post(`/cases/${id}/pretrial/assessment`, data);
export const fileDetentionPetition = (id, data) => api.post(`/cases/${id}/pretrial/petition`, data);
export const recordPretrialDecision = (id, data) => api.post(`/cases/${id}/pretrial/decision`, data);
export const getAllPretrial = () => api.get(`/pretrial`);
export const addCheckin = (id, data) => api.post(`/pretrial/${id}/checkin`, data);
export const addViolation = (id, data) => api.post(`/pretrial/${id}/violation`, data);
export const escalateViolation = (id) => api.post(`/pretrial/${id}/escalate`);
export const getReminders = () => api.get(`/reminders`);
export const getWarrants = (q) => api.get(`/warrants${q ? `?q=${encodeURIComponent(q)}` : ""}`);
export const issueWarrant = (data) => api.post(`/warrants`, data);
export const updateWarrant = (id, status) => api.patch(`/warrants/${id}`, { status });
export const logWarrantService = (id, data) => api.post(`/warrants/${id}/service`, data);
export const getWarrantRequests = () => api.get(`/warrant-requests`);
export const createWarrantRequest = (data) => api.post(`/warrant-requests`, data);
export const decideWarrantRequest = (id, data) => api.post(`/warrant-requests/${id}/decision`, data);
export const issueWarrantFromRequest = (id) => api.post(`/warrant-requests/${id}/issue`);
export const updateIntake = (id, data) => api.patch(`/cases/${id}/intake`, data);
export const chargingReview = (id, data) => api.post(`/cases/${id}/charging-review`, data);
export const saoChargeReviewQueue = () => api.get(`/sao/charge-review`);
export const grandJuryDecision = (id, data) => api.post(`/cases/${id}/grand-jury`, data);
export const clerkApproveFiling = (id, data) => api.post(`/cases/${id}/clerk-approve`, data);

// Jail Booking / Commitment portal (CCDOC)
export const getJailCommitments = () => api.get(`/jail/commitments`);
export const createJailCommitment = (data) => api.post(`/jail/commitments`, data);
export const bookJailCommitment = (id, data) => api.post(`/jail/commitments/${id}/book`, data);
export const updateJailStatus = (id, data) => api.patch(`/jail/commitments/${id}`, data);
export const mittimusUrl = (id) => `${API}/jail/commitments/${id}/mittimus?token=${tok()}`;
export const generateCaseDoc = (id, docType, data) => api.post(`/cases/${id}/documents/generate`, { docType, data });
export const editCaseDoc = (id, docType, data) => api.post(`/cases/${id}/documents/edit`, { docType, data });
export const caseDocUrl = (id, type) => `${API}/cases/${id}/documents/pdf?type=${encodeURIComponent(type)}&token=${tok()}`;
export const nolleProsequi = (id, data) => api.post(`/cases/${id}/nolle`, data);
export const getOffenseCatalog = () => api.get(`/offense-catalog`);
// Advanced CMS features
export const batchAction = (data) => api.post(`/batch/action`, data);
export const getBenchCalendar = (date) => api.get(`/bench/calendar${date ? `?date=${date}` : ""}`);
export const getAnalytics = () => api.get(`/analytics`);
export const getTasks = () => api.get(`/tasks`);
export const createTask = (data) => api.post(`/tasks`, data);
export const updateTask = (id, data) => api.patch(`/tasks/${id}`, data);
export const getWorkflows = () => api.get(`/workflows`);
export const updateWorkflow = (caseType, stages) => api.put(`/workflows/${encodeURIComponent(caseType)}`, { stages });
// Admin: user accounts & permissions
export const getPermissionsCatalog = () => api.get(`/permissions-catalog`);
export const getUsers = () => api.get(`/users`);
export const createUser = (data) => api.post(`/users`, data);
export const updateUser = (id, data) => api.patch(`/users/${id}`, data);
export const resetUserPassword = (id, password) => api.post(`/users/${id}/reset-password`, { password });
// Admin: court data
export const getCourtData = () => api.get(`/court-data`);
export const createCourtroom = (data) => api.post(`/courtrooms`, data);
export const updateCourtroom = (id, data) => api.patch(`/courtrooms/${id}`, data);
export const deleteCourtroom = (id) => api.delete(`/courtrooms/${id}`);
export const createDivision = (name) => api.post(`/divisions`, { name });
// Efficiency features: alerts, flags, appearances, speedy-trial, sentencing, call sheet
export const getAlerts = () => api.get(`/alerts`);
export const getCaseFlagsCatalog = () => api.get(`/case-flags-catalog`);
export const updateCaseFlags = (id, flags) => api.patch(`/cases/${id}/flags`, { flags });
export const fileAppearance = (id, data) => api.post(`/cases/${id}/appearance`, data);
export const imposeSentence = (id, data) => api.post(`/cases/${id}/sentence`, data);
export const callSheetUrl = (date, judge) => `${API}/call-sheet/pdf?date=${encodeURIComponent(date || "")}&judge=${encodeURIComponent(judge || "")}&token=${tok()}`;
// Plea negotiations, judge dashboard, offense sentencing reference
export const enterRuling = (id, data) => api.post(`/cases/${id}/ruling`, data);
export const setFirmDate = (id, data) => api.patch(`/cases/${id}/firm-date`, data);
export const getCourtReference = () => api.get(`/court-reference`);
export const addPlea = (id, data) => api.post(`/cases/${id}/plea`, data);
export const actPlea = (id, pleaId, status) => api.patch(`/cases/${id}/plea/${pleaId}`, { status });
export const getJudgeDashboard = () => api.get(`/judge/dashboard`);
export const getCaseAging = () => api.get(`/reports/case-aging`);
export const bulkReschedule = (data) => api.post(`/calendar/bulk-reschedule`, data);
export const getDocTemplates = () => api.get(`/document-templates`);
export const fileMotion = (id, data) => api.post(`/cases/${id}/motions`, data);
export const ruleMotion = (id, motionId, data) => api.post(`/cases/${id}/motions/${motionId}/rule`, data);
export const reviewMotion = (id, motionId, data) => api.post(`/cases/${id}/motions/${motionId}/review`, data);
export const syncStage = (id) => api.post(`/cases/${id}/sync-stage`);
export const continueCase = (id, data) => api.post(`/cases/${id}/continue`, data);
export const benchAppearance = (id, data) => api.post(`/cases/${id}/bench/appearance`, data);
export const benchOutcome = (id, data) => api.post(`/cases/${id}/bench/outcome`, data);
export const addExhibit = (id, data) => api.post(`/cases/${id}/exhibits`, data);
export const updateExhibit = (id, exId, data) => api.patch(`/cases/${id}/exhibits/${exId}`, data);
export const addCaseNote = (id, data) => api.post(`/cases/${id}/notes`, data);
export const fileProceedings = (id, data) => api.post(`/cases/${id}/proceedings/file`, data);
export const proceedingsPdfUrl = (id, date) => `${API}/cases/${id}/proceedings/pdf?date=${encodeURIComponent(date)}&token=${tok()}`;
export const getPublicCase = (caseNumber) => api.get(`/public/cases/${encodeURIComponent(caseNumber)}`);
export const getBookings = () => api.get(`/bookings`);
export const createBooking = (data) => api.post(`/bookings`, data);
export const updateBooking = (id, status) => api.patch(`/bookings/${id}`, { status });
export const fileBooking = (id) => api.post(`/bookings/${id}/file`);
export const getReports = () => api.get(`/reports`);
export const createReport = (data) => api.post(`/reports`, data);
export const createPaymentPlan = (data) => api.post(`/public/payment-plan`, data);
export const getMyCases = () => api.get(`/cases-mine`);

// Firearms (FOID/CCL) + background check
export const getFirearms = (q) => api.get(`/firearms${q ? `?q=${encodeURIComponent(q)}` : ""}`);
export const createFirearm = (data) => api.post(`/firearms`, data);
export const firearmAction = (id, data) => api.post(`/firearms/${id}/action`, data);
export const backgroundCheck = (q) => api.get(`/background-check?q=${encodeURIComponent(q)}`);
export const getIndividualProfile = (q) => api.get(`/individuals/profile?q=${encodeURIComponent(q)}`);
export const rapSheetUrl = (q) => `${API}/individuals/rap-sheet?q=${encodeURIComponent(q)}&token=${tok()}`;

// Filings
export const getFilings = () => api.get("/filings");
export const updateFiling = (id, status) => api.patch(`/filings/${id}`, { status });

// Payments
export const getPayments = () => api.get("/payments");
export const createPayment = (data) => api.post("/payments", data);

// Staff
export const getStaff = () => api.get("/staff");
export const createStaff = (data) => api.post("/staff", data);
export const updateStaff = (id, status) => api.patch(`/staff/${id}`, { status });

// Calendar
export const getCalendar = () => api.get("/calendar");

// PDF URLs (open directly in browser with token)
const tok = () => localStorage.getItem("odyssey_token") || "";
export const caseSummaryUrl = (id) => `${API}/cases/${id}/pdf?token=${tok()}`;
export const orderPdfUrl = (id, idx) => `${API}/cases/${id}/orders/${idx}/pdf?token=${tok()}`;
export const receiptUrl = (id) => `${API}/payments/${id}/receipt?token=${tok()}`;
export const pretrialOrderUrl = (id) => `${API}/cases/${id}/pretrial/order?token=${tok()}`;
export const pretrialConditionsSheetUrl = (id) => `${API}/cases/${id}/pretrial/conditions-sheet?token=${tok()}`;
export const casePacketUrl = (id) => `${API}/cases/${id}/packet?token=${tok()}`;
export const motionPdfUrl = (id, motionId) => `${API}/cases/${id}/motions/${motionId}/pdf?token=${tok()}`;

export default api;
